import {
  computed,
  Injectable,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { ProjectInfoStateInterface } from './project.interface';
import { ExpatClient } from '../../../core/api/models/expat-client';
import { PricingInformations } from '../../../core/api/models/pricing-informations';
import { ClientGender, PoliciesMode } from '../../helpers/client.utils';
import { AcsBeneficiaryClientModel } from '../../model/acs-project.model';
import { ExpatPricing } from '../../../core/api/models/expat-pricing';
import { Beneficiary } from '../../../core/api/models/beneficiary';

@Injectable({
  providedIn: 'root'
})
export class ProjectStateService {
  private projectInfos: ProjectInfoStateInterface = {
    pricing: null,
    producer_code: null,
    client: null,
    mode: null,
    subscription: null,
    beneficiaries: [],
    product: null,
    fingerprint: null,
    token: null,
    token_producer: null
  };

  private pricingInformations: PricingInformations = {} as PricingInformations;
  private expatClientInfos: AcsBeneficiaryClientModel =
    {} as AcsBeneficiaryClientModel;

  readonly keyPricingInformations = 'pricingInformations';
  readonly keyExpatClient = 'expatClientInfos';
  readonly keyExpatPricing = 'expatPricing';
  readonly keyUrlParamInfo = 'paramInfo';
  readonly projectBeneficiariesKey = 'projectBeneficiaries';
  readonly keyIsNewProject = 'isNewProject';
  readonly beneMatKey: string = 'benefmat';
  readonly keyGuard = 'path';

  private safeSetItem(key: string, value: unknown): void {
    if (value === undefined) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  private getParsedItem<T>(key: string): T | null {
    const item = sessionStorage.getItem(key);
    if (!item || item === 'undefined') {
      return null;
    }
    try {
      return JSON.parse(item) as T;
    } catch {
      return null;
    }
  }

  private isNewProject: WritableSignal<boolean> = signal(
    JSON.parse(sessionStorage.getItem(this.keyIsNewProject) ?? 'null') ??
      this.isPoliciesModeClient()
  );

  private isNewClient: WritableSignal<boolean> = signal(false);

  private expatPricing: WritableSignal<ExpatPricing> = signal(
    JSON.parse(sessionStorage.getItem(this.keyExpatPricing) ?? '{}')
  );

  private projectBeneficiaries: Signal<Beneficiary[]> = signal(
    this.selectProjectBeneficiaries()
  );

  private projectInfosState: WritableSignal<ProjectInfoStateInterface> = signal(
    this.projectInfos
  );

  /** set and update **/

  public updateData(newData: ProjectInfoStateInterface) {
    this.projectInfosState.set({ ...newData });
  }

  public updateProjectInfo(projectInfo: Partial<ProjectInfoStateInterface>) {
    this.projectInfosState.set({
      ...this.projectInfosState(),
      ...projectInfo
    });
    this.safeSetItem(this.keyExpatPricing, projectInfo.pricing);
    this.safeSetItem(this.projectBeneficiariesKey, projectInfo.beneficiaries);
    this.safeSetItem(
      this.keyPricingInformations,
      projectInfo.pricing
        ? { ...projectInfo.pricing, currency: projectInfo.product ?? undefined }
        : undefined
    );

    let beneMatCategory =  projectInfo.beneficiaries
      ?.map(b => b.acs_expat_beneficiary_maternity_option ? b.category : null)
      .filter(b => b !== null)[0] ?? null;
    sessionStorage.setItem(this.beneMatKey, JSON.stringify(beneMatCategory));
  }

  public updateClient(client: ExpatClient) {
    this.projectInfosState.set({ ...this.projectInfos, client: client });
    sessionStorage.setItem(this.keyExpatClient, JSON.stringify(client));
  }

  public setGuardPath(path: string) {
    sessionStorage.setItem(this.keyGuard, path);
  }

  public updatePricingInformations(
    infosToUpdate: Partial<PricingInformations> & { currency?: string }
  ): void {
    const storedInfos = this.getPricingInformations();
    const clientStoredInfo = this.selectExpatClientInfos();
    if (storedInfos) {
      if (
        clientStoredInfo &&
        !this.selectProjectBeneficiaries() &&
        clientStoredInfo.title !== ClientGender.FEMALE
      ) {
        infosToUpdate.expat_project_maternity_option = null;
      }
      this.pricingInformations = {
        ...storedInfos,
        ...infosToUpdate
      };
    } else {
      this.pricingInformations = {
        ...this.pricingInformations,
        ...infosToUpdate
      };
    }
    sessionStorage.setItem(
      this.keyPricingInformations,
      JSON.stringify(this.pricingInformations)
    );
  }

  public setProjectBeneficiaries(
    beneficiaries: Array<Beneficiary> | undefined
  ): void {
    this.safeSetItem(this.projectBeneficiariesKey, beneficiaries);
  }

  public setBeneficiaryHasMaternityOption(beneMatCategory: string | undefined) {
    this.safeSetItem(this.beneMatKey, beneMatCategory);
  }

  public setExpatPricing(data: ExpatPricing): void {
    this.expatPricing.set(data);
    sessionStorage.setItem(this.keyExpatPricing, JSON.stringify(data));
  }

  public updateExpatClientInfos(
    infoToUpdate: Partial<AcsBeneficiaryClientModel>,
    forceUpdateBeneficiaries: boolean
  ): void {
    let beneficiaries: Beneficiary[] = [];
    if (
      infoToUpdate.beneficiaries &&
      infoToUpdate.beneficiaries.length > 0 &&
      this.projectBeneficiaries() &&
      this.projectBeneficiaries().length > 0 &&
      !forceUpdateBeneficiaries
    ) {
      beneficiaries = infoToUpdate.beneficiaries?.map((obj) => {
        const index = this.projectBeneficiaries().findIndex(
          (b) => b.category === obj.category && b.birth_date === obj.birth_date
        );
        return {
          ...obj,
          ...(this.projectBeneficiaries()![index] || {})
        };
      });
    } else if (infoToUpdate.beneficiaries) {
      beneficiaries = infoToUpdate.beneficiaries;
    }
    infoToUpdate.beneficiaries = beneficiaries;
    this.expatClientInfos = {
      ...this.expatClientInfos,
      ...infoToUpdate
    };
    sessionStorage.setItem(
      this.keyExpatClient,
      JSON.stringify(this.expatClientInfos)
    );
  }

  public setIsNewProject(value: boolean) {
    this.isNewProject.set(value);
  }

  /** get and select **/

  public getGuardPath(): string | null {
    return sessionStorage.getItem(this.keyGuard);
  }

  public selectIsNewProject() {
    return computed(() => this.isNewProject());
  }

  public selectIsNewClient() {
    return this.isNewClient;
  }

  public setIsNewClient(value: boolean) {
    this.isNewClient.set(value);
  }

  public selectExpatClientInfos(): ExpatClient | null {
    return this.getParsedItem<ExpatClient>(this.keyExpatClient);
  }

  public selectProjectBeneficiaries(): Beneficiary[] {
    return this.getParsedItem<Beneficiary[]>(this.projectBeneficiariesKey) ?? [];
  }

  public selectExpatPricing() {
    return computed(() => this.expatPricing());
  }

  public getPricingInformations():
    | (PricingInformations & {
        beneficiaries?: string;
        currency?: string;
      })
    | null {
    return this.getParsedItem<PricingInformations>(this.keyPricingInformations);
  }

  public getBeneficiaryHasMaternityOption(): string | null {
    return this.getParsedItem<string>(this.beneMatKey);
  }

  private isPoliciesModeClient() {
    const param = this.getParsedItem<{ entity: PoliciesMode }>(
      this.keyUrlParamInfo
    );
    return (param ? param.entity : null) === PoliciesMode.CLIENTS;
  }

  public reset() {
    this.projectInfosState.set({
      pricing: null,
      producer_code: this.projectInfosState().producer_code,
      client: null,
      mode: null,
      subscription: null,
      beneficiaries: [],
      product: null,
      fingerprint: null,
      token: null,
      token_producer: this.projectInfosState().token_producer
    });
    this.pricingInformations = {} as PricingInformations;
    this.expatClientInfos = {} as AcsBeneficiaryClientModel;
    this.isNewClient.set(true);
    sessionStorage.removeItem(this.keyPricingInformations);
    sessionStorage.removeItem(this.keyExpatClient);
    sessionStorage.removeItem(this.keyExpatPricing);
    sessionStorage.removeItem(this.projectBeneficiariesKey);
    sessionStorage.removeItem(this.keyIsNewProject);
    sessionStorage.removeItem(this.beneMatKey);
  }
}
